import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoSearch, IoClose, IoCheckmarkCircle, IoCloseCircle, IoTrashOutline, IoKeyOutline } from 'react-icons/io5';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { ROLES, ROLE_LABELS, APP_CONFIG } from '../../../config/appConfig';
import Spinner from '../../../components/common/Spinner';

const TABS = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '승인대기' },
  { key: 'approved', label: '승인완료' },
  { key: 'rejected', label: '거절' },
];

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const [modal, setModal] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null); // 승인 팝업 대상 유저
  const [selectedRole, setSelectedRole] = useState('');

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
      list.sort((a, b) => {
        const da = a.createdAt?.toDate?.() || new Date(0);
        const db2 = b.createdAt?.toDate?.() || new Date(0);
        return db2 - da;
      });
      setUsers(list);
    } catch (err) {
      console.error('회원 목록 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const filtered = users.filter(u => {
    if (!u.name && !u.companyName && !u.phone) return false;
    if (tab !== 'all' && u.status !== tab) return false;
    if (search && !(u.companyName || '').includes(search) && !(u.name || '').includes(search) && !(u.loginId || '').includes(search)) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openApprovePopup = (user) => {
    setApproveTarget(user);
    setSelectedRole('');
  };

  const handleApprove = async () => {
    if (!selectedRole || !approveTarget) return;
    try {
      const uid = approveTarget.uid;
      await updateDoc(doc(db, 'users', uid), { status: 'approved', role: selectedRole, updatedAt: serverTimestamp() });
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, status: 'approved', role: selectedRole } : u));
      if (modal?.uid === uid) setModal(prev => ({ ...prev, status: 'approved', role: selectedRole }));

      setApproveTarget(null);
      setSelectedRole('');
    } catch (err) {
      console.error('승인 실패:', err);
    }
  };

  const [rejectTarget, setRejectTarget] = useState(null);

  const handleReject = async (uid) => {
    try {
      await updateDoc(doc(db, 'users', uid), { status: 'rejected', updatedAt: serverTimestamp() });
      setUsers(prev => prev.map(u => u.uid === uid ? { ...u, status: 'rejected' } : u));
      if (modal?.uid === uid) setModal(prev => ({ ...prev, status: 'rejected' }));

      setRejectTarget(null);
      setModal(null);
    } catch (err) {
      console.error('거절 실패:', err);
    }
  };

  const [pwTarget, setPwTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) { setPwError('비밀번호를 4자 이상 입력해주세요.'); return; }
    if (newPassword !== pwConfirm) { setPwError('비밀번호가 일치하지 않습니다.'); return; }
    setPwSaving(true);
    setPwError('');
    try {
      const res = await fetch(APP_CONFIG.resetPasswordUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: pwTarget.uid, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '비밀번호 변경 실패');
      setUsers(prev => prev.map(u => u.uid === pwTarget.uid ? { ...u, password: newPassword } : u));
      setPwTarget(null);
      setNewPassword('');
      setPwConfirm('');
    } catch (err) {
      console.error('비밀번호 변경 실패:', err);
      setPwError('변경 실패. 다시 시도해주세요.');
    } finally {
      setPwSaving(false);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');

  const handleDelete = async (user) => {
    setDeleteTarget(null);
    setDeleting(true);
    const uid = user.uid;

    const tryDeleteCollection = async (colName, field) => {
      try {
        const snap = await getDocs(query(collection(db, colName), where(field, '==', uid)));
        for (const d of snap.docs) await deleteDoc(d.ref);
      } catch (e) {
        console.warn(`${colName} 삭제 스킵:`, e.message);
      }
    };

    try {
      setDeleteProgress('일정 데이터 삭제 중...');
      await tryDeleteCollection('schedules', 'assignedTo');

      setDeleteProgress('시공 데이터 삭제 중...');
      await tryDeleteCollection('constructions', 'contractorUid');

      setDeleteProgress('주문 데이터 삭제 중...');
      await tryDeleteCollection('orders', 'contractorUid');

      setDeleteProgress('게시글 삭제 중...');
      await tryDeleteCollection('posts', 'authorUid');

      setDeleteProgress('인덱스 정리 중...');
      if (user.phoneE164) {
        try { await deleteDoc(doc(db, 'phones', user.phoneE164)); } catch {}
        try { await deleteDoc(doc(db, 'users_by_phone', user.phoneE164)); } catch {}
      }

      setDeleteProgress('회원 정보 삭제 중...');
      await deleteDoc(doc(db, 'users', uid));

      setUsers(prev => prev.filter(u => u.uid !== uid));
      setDeleteProgress('삭제 완료');
    } catch (err) {
      console.error('삭제 실패:', err);
      setDeleteProgress('삭제 중 오류 발생');
    } finally {
      setTimeout(() => { setDeleting(false); setDeleteProgress(''); }, 800);
    }
  };

  const countByStatus = (s) => users.filter(u => u.status === s).length;
  const getRoleLabel = (role) => ROLE_LABELS[role] || role || '-';
  const getLoginId = (u) => u.loginId || '소셜 로그인으로만 가입';
  const getAuthType = (u) => {
    const PROVIDER_LABELS = { email: '아이디', google: '구글', kakao: '카카오톡' };
    const types = [];
    // providers 배열이 있으면 우선 사용
    if (u.providers && u.providers.length > 0) {
      u.providers.forEach(p => {
        const label = PROVIDER_LABELS[p] || p;
        if (!types.includes(label)) types.push(label);
      });
    } else {
      // 레거시: providers 배열 없는 기존 데이터 호환
      if (u.loginId) types.push('아이디');
      if (u.provider === 'google') types.push('구글');
      if (u.provider === 'kakao') types.push('카카오톡');
      if (u.provider === 'email' && !u.loginId) types.push('아이디');
    }
    if (u.loginId && !types.includes('아이디')) types.unshift('아이디');
    return types.join(', ') || '-';
  };
  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('ko-KR') + ' ' + d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <Container><Spinner variant="spin" size="md" text="회원 목록 로딩 중..." fullPage /></Container>;

  return (
    <Container>
      <PageHeader>
        <SearchBox>
          <IoSearch size={18} color="#8C9AA4" />
          <SearchInput placeholder="업체명, 이름 검색" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </SearchBox>
      </PageHeader>

      <TabRow>
        {TABS.map(t => (
          <Tab key={t.key} $active={tab === t.key} onClick={() => { setTab(t.key); setPage(1); }}>
            {t.label}
            {t.key !== 'all' && <TabCount $active={tab === t.key}>{countByStatus(t.key)}</TabCount>}
          </Tab>
        ))}
      </TabRow>

      <TableWrap style={{ position: 'relative' }}>
        {deleting && (
          <DeleteOverlay>
            <Spinner variant="dots" size="sm" />
            <DeleteProgressText>{deleteProgress}</DeleteProgressText>
          </DeleteOverlay>
        )}
        <Table>
          <thead>
            <Tr>
              <Th>업체명</Th><Th>대표자</Th><Th>아이디</Th><Th>비밀번호</Th><Th>가입형태</Th><Th>역할</Th><Th>연락처</Th><Th>가입일시</Th><Th>상태</Th><Th>액션</Th>
            </Tr>
          </thead>
          <tbody>
            {paged.map(u => (
              <Tr key={u.uid} onClick={() => setModal(u)} style={{ cursor: 'pointer' }}>
                <Td>{u.companyName || '-'}</Td>
                <Td>{u.name || '-'}</Td>
                <Td $small={!u.loginId}>{getLoginId(u)}</Td>
                <Td onClick={e => e.stopPropagation()}>
                  {u.password ? (
                    <PwCell>
                      <span>***</span>
                      <PwChangeBtn onClick={() => { setPwTarget(u); setNewPassword(''); setPwConfirm(''); setPwError(''); setSearch(''); }}><IoKeyOutline size={13} /> 변경</PwChangeBtn>
                    </PwCell>
                  ) : <span style={{ color: '#8C9AA4', fontSize: '11px' }}>-</span>}
                </Td>
                <Td>{getAuthType(u)}</Td>
                <Td>{getRoleLabel(u.role)}</Td>
                <Td>{u.phone || u.phoneE164 || '-'}</Td>
                <Td>{formatDate(u.createdAt)}</Td>
                <Td><StatusBadge $status={u.status}>{u.status === 'pending' ? '대기' : u.status === 'approved' ? '승인' : u.status === 'rejected' ? '거절' : '-'}</StatusBadge></Td>
                <Td onClick={e => e.stopPropagation()}>
                  <ActionRow>
                    {u.status !== 'approved' && (
                      <ActionBtn $approve onClick={() => openApprovePopup(u)}><IoCheckmarkCircle size={16} /> 승인</ActionBtn>
                    )}
                    {u.status !== 'rejected' && u.status !== 'approved' && (
                      <ActionBtn onClick={() => setRejectTarget(u)}><IoCloseCircle size={16} /> 거절</ActionBtn>
                    )}
                    <ActionBtn $delete onClick={() => setDeleteTarget(u)}><IoTrashOutline size={14} /> 삭제</ActionBtn>
                  </ActionRow>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        {filtered.length === 0 && <Empty>해당 회원이 없습니다</Empty>}
      </TableWrap>

      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page === 1} onClick={() => setPage(1)}>«</PageBtn>
          <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((p, i) => p === '...' ? <PageDots key={`dots-${i}`}>…</PageDots> : <PageNum key={p} $active={page === p} onClick={() => setPage(p)}>{p}</PageNum>)}
          <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</PageBtn>
          <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
          <PageInfo>{filtered.length}건 중 {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)}</PageInfo>
        </Pagination>
      )}

      {modal && (
        <Overlay onClick={() => setModal(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>회원 상세</ModalTitle>
              <CloseBtn onClick={() => setModal(null)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <DetailRow><DetailLabel>업체명</DetailLabel><DetailValue>{modal.companyName || '-'}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>대표자</DetailLabel><DetailValue>{modal.name || '-'}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>아이디</DetailLabel><DetailValue>{getLoginId(modal)}</DetailValue></DetailRow>
              {modal.password && (
                <DetailRow>
                  <DetailLabel>비밀번호</DetailLabel>
                  <DetailValue style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>***</span>
                    <PwChangeBtn onClick={() => { setPwTarget(modal); setNewPassword(''); setPwConfirm(''); setPwError(''); setSearch(''); }}><IoKeyOutline size={13} /> 변경</PwChangeBtn>
                  </DetailValue>
                </DetailRow>
              )}
              <DetailRow><DetailLabel>가입형태</DetailLabel><DetailValue>{getAuthType(modal)}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>역할</DetailLabel><DetailValue>{getRoleLabel(modal.role)}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>연락처</DetailLabel><DetailValue>{modal.phone || modal.phoneE164 || '-'}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>주소</DetailLabel><DetailValue>{modal.address || '-'}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>가입일시</DetailLabel><DetailValue>{formatDate(modal.createdAt)}</DetailValue></DetailRow>
              <DetailRow><DetailLabel>상태</DetailLabel><DetailValue><StatusBadge $status={modal.status}>{modal.status === 'pending' ? '대기' : modal.status === 'approved' ? '승인' : modal.status === 'rejected' ? '거절' : '-'}</StatusBadge></DetailValue></DetailRow>
              {modal.businessLicenseUrl && (
                <>
                  <DetailRow><DetailLabel>사업자등록증</DetailLabel><DetailValue>{modal.ocrVerified ? 'OCR 확인됨' : 'OCR 미확인'}</DetailValue></DetailRow>
                  <LicenseImgWrap>
                    <LicenseImg src={modal.businessLicenseUrl} alt="사업자등록증" />
                  </LicenseImgWrap>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              {modal.status !== 'approved' && (
                <FooterBtn $approve onClick={() => { const u = modal; setModal(null); openApprovePopup(u); }}>승인</FooterBtn>
              )}
              {modal.status !== 'rejected' && modal.status !== 'approved' && (
                <FooterBtn onClick={() => { const u = modal; setModal(null); setRejectTarget(u); }}>거절</FooterBtn>
              )}
              <FooterBtn $delete onClick={() => { setModal(null); setDeleteTarget(modal); }}>삭제</FooterBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
      {deleteTarget && (
        <Overlay onClick={() => setDeleteTarget(null)} style={{ zIndex: 300 }}>
          <ApprovePopup onClick={e => e.stopPropagation()}>
            <PopupTitle>회원 삭제</PopupTitle>
            <PopupDesc>
              <strong>{deleteTarget.name}</strong>
              {deleteTarget.companyName ? ` (${deleteTarget.companyName})` : ''}
              님의 모든 정보를 삭제합니다.<br />
              <DeleteWarning>일정, 시공, 주문, 게시글 등 모든 데이터가 영구 삭제되며 복구할 수 없습니다.</DeleteWarning>
            </PopupDesc>
            <PopupActions>
              <PopupCancelBtn onClick={() => setDeleteTarget(null)}>취소</PopupCancelBtn>
              <PopupRejectBtn onClick={() => handleDelete(deleteTarget)}>삭제하기</PopupRejectBtn>
            </PopupActions>
          </ApprovePopup>
        </Overlay>
      )}

      {rejectTarget && (
        <Overlay onClick={() => setRejectTarget(null)} style={{ zIndex: 300 }}>
          <ApprovePopup onClick={e => e.stopPropagation()}>
            <PopupTitle>회원 거절</PopupTitle>
            <PopupDesc>
              <strong>{rejectTarget.name}</strong>
              {rejectTarget.companyName ? ` (${rejectTarget.companyName})` : ''}
              님의 가입을 거절하시겠습니까?
            </PopupDesc>
            <PopupActions>
              <PopupCancelBtn onClick={() => setRejectTarget(null)}>취소</PopupCancelBtn>
              <PopupRejectBtn onClick={() => handleReject(rejectTarget.uid)}>거절하기</PopupRejectBtn>
            </PopupActions>
          </ApprovePopup>
        </Overlay>
      )}

      {pwTarget && (
        <Overlay onClick={() => setPwTarget(null)} style={{ zIndex: 300 }}>
          <ApprovePopup onClick={e => e.stopPropagation()}>
            <PopupTitle>비밀번호 변경</PopupTitle>
            <PopupDesc>
              <strong>{pwTarget.name}</strong>
              {pwTarget.companyName ? ` (${pwTarget.companyName})` : ''}
              님의 비밀번호를 변경합니다.
            </PopupDesc>
            <PwInputGroup>
              <PwInput type="password" placeholder="새 비밀번호" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <PwInput type="password" placeholder="비밀번호 확인" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} />
              {pwError && <PwErrorText>{pwError}</PwErrorText>}
            </PwInputGroup>
            <PopupActions>
              <PopupCancelBtn onClick={() => setPwTarget(null)}>취소</PopupCancelBtn>
              <PopupConfirmBtn onClick={handleChangePassword} disabled={pwSaving || !newPassword}>
                {pwSaving ? '변경 중...' : '변경하기'}
              </PopupConfirmBtn>
            </PopupActions>
          </ApprovePopup>
        </Overlay>
      )}

      {approveTarget && (
        <Overlay onClick={() => setApproveTarget(null)} style={{ zIndex: 300 }}>
          <ApprovePopup onClick={e => e.stopPropagation()}>
            <PopupTitle>회원 승인</PopupTitle>
            <PopupDesc>
              <strong>{approveTarget.name}</strong>
              {approveTarget.companyName ? ` (${approveTarget.companyName})` : ''}
              님에게 부여할 권한을 선택해주세요.
            </PopupDesc>
            <RoleBtnGroup>
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <RoleBtn key={k} $active={selectedRole === k} onClick={() => setSelectedRole(k)}>
                  {v}
                </RoleBtn>
              ))}
            </RoleBtnGroup>
            <PopupActions>
              <PopupCancelBtn onClick={() => setApproveTarget(null)}>취소</PopupCancelBtn>
              <PopupConfirmBtn onClick={handleApprove} disabled={!selectedRole}>승인하기</PopupConfirmBtn>
            </PopupActions>
          </ApprovePopup>
        </Overlay>
      )}
    </Container>
  );
};

export default AdminUsersPage;

const Pagination = styled.div`display: flex; align-items: center; justify-content: center; gap: 4px; padding: 20px 0 8px;`;
const PageBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 14px;
  background: #fff; color: #666; border: none;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:disabled { opacity: 0.3; cursor: default; }
  &:hover:not(:disabled) { background: #F0F0EC; }
`;
const PageNum = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 13px; font-weight: 500;
  border: none; display: flex; align-items: center; justify-content: center;
  background: ${p => p.$active ? '#4A6741' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
  &:hover { background: ${p => p.$active ? '#3B5534' : '#F0F0EC'}; }
`;
const PageDots = styled.span`font-size: 13px; color: #8C9AA4; padding: 0 4px;`;
const PageInfo = styled.span`font-size: 12px; color: #8C9AA4; margin-left: 12px;`;
const Container = styled.div`padding: 32px;`;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`;
const SearchBox = styled.div`
  display: flex; align-items: center; gap: 8px; background: #fff; border: none;
  border-radius: 10px; padding: 8px 14px; width: 260px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;
const SearchInput = styled.input`border: none; outline: none; font-size: 13px; flex: 1; background: transparent;`;

const TabRow = styled.div`display: flex; gap: 10px; margin-bottom: 20px;`;
const Tab = styled.button`
  padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 500;
  display: flex; align-items: center; gap: 6px;
  background: ${p => p.$active ? '#8C9AA4' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: none;
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
`;
const TabCount = styled.span`
  font-size: 11px; padding: 1px 7px; border-radius: 10px;
  background: ${p => p.$active ? 'rgba(255,255,255,0.25)' : '#F0F0EC'};
`;

const TableWrap = styled.div`background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); overflow: hidden;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Tr = styled.tr`
  position: relative; transition: all 0.15s;
  &:not(:last-child) td { border-bottom: 1px solid #F0F0EC; }
  &:hover td { background: #EDF2EB; }
  &:hover td:first-child { box-shadow: inset 3px 0 0 #4A6741; }
  &:active td { background: #E3EBDF; }
`;
const Th = styled.th`text-align: left; font-size: 13px; color: #8C9AA4; font-weight: 400; padding: 12px 16px; border-bottom: 1px solid #E8E8E4; background: #FAFAF7;`;
const Td = styled.td`
  font-size: ${p => p.$small ? '12px' : '14px'};
  color: ${p => p.$small ? '#8C9AA4' : '#1E2A32'};
  padding: 14px 16px; font-weight: 400;
`;
const Empty = styled.div`text-align: center; padding: 48px; color: #8C9AA4; font-size: 14px;`;

const StatusBadge = styled.span`
  display: inline-block; font-size: 12px; padding: 5px 14px; border-radius: 4px; font-weight: 400; letter-spacing: 0.02em;
  background: ${p => p.$status === 'pending' ? '#E8DFD0' : p.$status === 'approved' ? '#D4E2CF' : '#E0E0E0'};
  color: ${p => p.$status === 'pending' ? '#6B5A3E' : p.$status === 'approved' ? '#3D6634' : '#666'};
`;
const ActionRow = styled.div`display: flex; gap: 6px;`;
const ActionBtn = styled.button`
  display: flex; align-items: center; gap: 4px; font-size: 12px; padding: 5px 12px; border-radius: 8px;
  background: ${p => p.$approve ? '#F0F0F0' : p.$delete ? '#FDF2F2' : '#F5F5F5'};
  color: ${p => p.$approve ? '#333' : p.$delete ? '#C0392B' : '#8C9AA4'};
  font-weight: 400;
  &:hover { opacity: 0.8; }
`;

const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const Modal = styled.div`background: #fff; border-radius: 16px; width: 480px; max-height: 80vh; overflow-y: auto;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #F0F0EC;`;
const ModalTitle = styled.h3`font-size: 18px; font-weight: 500;`;
const CloseBtn = styled.button`color: #8C9AA4; display: flex;`;
const ModalBody = styled.div`padding: 24px;`;
const DetailRow = styled.div`display: flex; padding: 10px 0; border-bottom: 1px solid #F8F8F5;`;
const DetailLabel = styled.div`width: 80px; font-size: 13px; color: #8C9AA4; flex-shrink: 0;`;
const DetailValue = styled.div`font-size: 14px; color: #1E2A32; font-weight: 400;`;
const ModalFooter = styled.div`display: flex; gap: 10px; padding: 16px 24px; border-top: 1px solid #F0F0EC;`;
const FooterBtn = styled.button`
  flex: 1; padding: 10px 20px; border-radius: 10px; font-size: 14px; font-weight: 400;
  background: ${p => p.$approve ? '#E8EDE6' : p.$delete ? '#FDF2F2' : '#F5F5F5'};
  color: ${p => p.$approve ? '#5A7A52' : p.$delete ? '#C0392B' : '#8C9AA4'};
`;

const ApprovePopup = styled.div`
  background: #fff; border-radius: 16px; width: 360px; padding: 28px 24px;
`;
const PopupTitle = styled.p`
  font-size: 16px; font-weight: 500; color: #1E2A32; margin-bottom: 8px;
`;
const PopupDesc = styled.p`
  font-size: 13px; color: #8C9AA4; margin-bottom: 20px; line-height: 1.5;
  strong { color: #1E2A32; font-weight: 500; }
`;
const RoleBtnGroup = styled.div`
  display: flex; gap: 10px; margin-bottom: 24px;
`;
const RoleBtn = styled.button`
  flex: 1; padding: 14px; border-radius: 12px; font-size: 14px; font-weight: 400;
  border: 1.5px solid ${p => p.$active ? '#4A6741' : '#E8E8E4'};
  background: ${p => p.$active ? '#EDF2EB' : '#fff'};
  color: ${p => p.$active ? '#4A6741' : '#666'};
  transition: all 0.15s;
`;
const PopupActions = styled.div`
  display: flex; gap: 10px;
`;
const PopupCancelBtn = styled.button`
  flex: 1; padding: 12px; border-radius: 10px; font-size: 14px; font-weight: 400;
  background: #F5F5F5; color: #8C9AA4;
`;
const PopupRejectBtn = styled.button`
  flex: 1; padding: 12px; border-radius: 10px; font-size: 14px; font-weight: 400;
  background: #F5EAEA; color: #C0392B;
`;
const PopupConfirmBtn = styled.button`
  flex: 1; padding: 12px; border-radius: 10px; font-size: 14px; font-weight: 400;
  background: #E8EDE6; color: #5A7A52;
  &:disabled { opacity: 0.4; }
`;

const DeleteOverlay = styled.div`
  position: absolute; inset: 0; background: rgba(255,255,255,0.85); z-index: 10;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
  border-radius: 14px;
`;
const DeleteProgressText = styled.p`
  font-size: 13px; color: #666; font-weight: 400;
`;
const DeleteWarning = styled.span`
  display: block; margin-top: 8px; font-size: 12px; color: #C0392B; line-height: 1.6;
`;

const PwCell = styled.div`
  display: flex; align-items: center; gap: 8px;
`;
const PwChangeBtn = styled.button`
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 11px; padding: 3px 8px; border-radius: 6px;
  background: #F0F0F0; color: #666; font-weight: 400;
  white-space: nowrap;
  &:hover { background: #E8E8E4; }
`;
const PwInputGroup = styled.div`
  display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px;
`;
const PwInput = styled.input`
  width: 100%; padding: 12px 14px; border-radius: 10px; font-size: 14px;
  border: 1.5px solid #E8E8E4; outline: none; box-sizing: border-box;
  &:focus { border-color: #4A6741; }
`;
const PwErrorText = styled.p`
  font-size: 12px; color: #C0392B; margin: 0;
`;

const LicenseImgWrap = styled.div`
  margin-top: 12px; border-radius: 12px; overflow: hidden; border: 1px solid #F0F0EC;
`;
const LicenseImg = styled.img`
  width: 100%; max-height: 300px; object-fit: contain; background: #FAFAF7;
`;
